import type { QueryClient } from '@tanstack/react-query';

/**
 * Invalidate every ticket-related query key across the desk list
 * (TicketsScreen), the detail view, the home summary, the field
 * worker's "My Tickets" view (FieldTicketsScreen), and the editor's
 * own copy of the ticket (NewTicketScreen). Call this in the
 * onSuccess of every ticket mutation so all views reflect the change
 * without a manual refresh.
 */
export function invalidateTickets(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['tickets'] });
  queryClient.invalidateQueries({ queryKey: ['ticket-detail'] });
  queryClient.invalidateQueries({ queryKey: ['home-tickets'] });
  queryClient.invalidateQueries({ queryKey: ['field-my-tickets'] });
  // The editor's own read (NewTicketScreen.tsx `['ticket-edit', id]`). It was
  // missing, and with the app's 30s default staleTime (App.tsx) that made the
  // editor serve a cached PRE-SAVE ticket for half a minute after a save.
  //
  // That is not cosmetic now that an edit can revert a status: save an
  // office-approved ticket (API reverts it to field_approved), re-enter the
  // editor inside 30s, and it still reads `office_approved` — so the revert
  // warning fires a second time for a revert that will not happen, and the
  // cleared customer fields still look present. The warning below keys off this
  // query's status, so a stale read makes it lie.
  queryClient.invalidateQueries({ queryKey: ['ticket-edit'] });
  // The billing pre-flight flags (`['billing-flags', id]`, api#908). The API
  // re-runs the checks on a version change, and its read runs them inline when
  // the stored run is older than the ticket — so a refetch after a save is
  // what brings the new checks in, instead of the last version's.
  queryClient.invalidateQueries({ queryKey: ['billing-flags'] });
}
